/** Reader — in-browser viewer for library books and research papers.
 *  Renders `body_html` / `description_html` in a long-form, focused typographic layout.
 *  When a real PDF media is attached, falls back to embedding it.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, ArrowLeft, BookOpen, Maximize2 } from "lucide-react";
import {
  Container, Section, Eyebrow, Button, Card, CardBody, LoadingState, ErrorState, Badge, Breadcrumb,
} from "../../components/ui";
import { api, formatApiErrorDetail } from "../../lib/api";

export function ReaderPage({ kind = "book" }) {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [fontSize, setFontSize] = useState(16);

  useEffect(() => {
    const path = kind === "book" ? `/books/${slug}` : `/research/${slug}`;
    api.get(path).then((r) => setDoc(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [kind, slug]);

  if (err) return <Section padded="sm"><Container><ErrorState description={err} /></Container></Section>;
  if (!doc) return <Section padded="sm"><Container><LoadingState /></Container></Section>;

  const downloadUrl = doc.pdf_url || doc.google_drive_url;
  const longHtml = doc.body_html || doc.description_html || (doc.abstract && `<p>${doc.abstract}</p>`) || (doc.summary && `<p>${doc.summary}</p>`) || "";

  // If a real PDF URL exists, embed it.
  if (downloadUrl && /\.pdf($|\?)/i.test(downloadUrl)) {
    return (
      <Section padded="none" className="pt-6 pb-12" data-testid={`reader-pdf-${kind}`}>
        <Container>
          <div className="flex items-center justify-between gap-4 mb-4">
            <Link to={kind === "book" ? `/library/${slug}` : `/research/${slug}`} className="text-[13px] text-mauve hover:underline inline-flex items-center gap-1.5">
              <ArrowLeft size={14}/> Back to detail
            </Link>
            <Button asChild size="sm" variant="ghost">
              <a href={downloadUrl} download target="_blank" rel="noopener noreferrer"><Download size={14}/> Download PDF</a>
            </Button>
          </div>
          <iframe src={downloadUrl} title={doc.title} className="w-full h-[80vh] rounded-2xl border border-line-2 bg-surface" />
        </Container>
      </Section>
    );
  }

  // Otherwise render HTML/long-form reader
  return (
    <Section padded="sm" data-testid={`reader-${kind}`}>
      <Container>
        <Breadcrumb items={[
          { label: "Home", to: "/" },
          { label: kind === "book" ? "Library" : "Research", to: kind === "book" ? "/library" : "/research" },
          { label: doc.title, to: `${kind === "book" ? "/library" : "/research"}/${slug}` },
          { label: "Read" },
        ]} />

        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Eyebrow>{kind === "book" ? "Reader" : "Paper"}</Eyebrow>
            <h1 className="font-display text-[clamp(28px,3.8vw,46px)] tracking-[-0.01em] mt-3 leading-tight">{doc.title}</h1>
            {doc.authors?.length > 0 && <div className="text-[13px] text-text-dim mt-2">{doc.authors.join(" · ")}</div>}
            {doc.journal && <Badge size="sm" variant="muted" className="mt-3">{doc.journal}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setFontSize((s) => Math.max(13, s - 1))} className="w-9 h-9 rounded-full border border-line-2 text-text-dim hover:text-text hover:border-mauve text-[14px]" aria-label="Smaller text" data-testid="reader-font-down">A-</button>
            <button onClick={() => setFontSize((s) => Math.min(22, s + 1))} className="w-9 h-9 rounded-full border border-line-2 text-text-dim hover:text-text hover:border-mauve text-[14px]" aria-label="Larger text" data-testid="reader-font-up">A+</button>
            {downloadUrl && (
              <Button asChild size="sm" variant="ghost">
                <a href={downloadUrl} download target="_blank" rel="noopener noreferrer"><Download size={14}/> Download</a>
              </Button>
            )}
          </div>
        </div>

        <Card variant="elevated" className="mt-8">
          <CardBody className="p-8 md:p-12">
            {longHtml ? (
              <article
                className="prose-nvl max-w-[72ch] mx-auto text-text-dim"
                style={{ fontSize: `${fontSize}px`, lineHeight: 1.85 }}
                data-testid={`reader-body-${kind}`}
                dangerouslySetInnerHTML={{ __html: longHtml }}
              />
            ) : (
              <div className="text-center py-12">
                <BookOpen size={36} className="mx-auto text-text-faint mb-3" />
                <p className="text-text-dim text-[14px]">Full text not yet uploaded for this {kind}.</p>
                <p className="text-text-faint text-[12.5px] mt-1">Once an admin attaches a PDF or full body, the reader will open here.</p>
              </div>
            )}
          </CardBody>
        </Card>

        {kind === "research" && doc.citation && (
          <Card variant="default" className="mt-6">
            <CardBody>
              <Eyebrow>Citation</Eyebrow>
              <p className="mt-3 text-[13.5px] text-text-dim font-mono">{doc.citation}</p>
            </CardBody>
          </Card>
        )}
      </Container>
    </Section>
  );
}
